import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ProgressRing } from '../components/ui/ProgressRing';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
    BookOpen,
    TrendingUp,
    Target,
    Award,
    Lock,
    CheckCircle,
    Play,
    Clock,
    ChevronRight,
    ArrowLeft,
    Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const levelConfig = {
    basic: { 
        icon: BookOpen, 
        color: 'emerald', 
        gradient: 'from-emerald-500 to-emerald-600',
        bgGradient: 'from-emerald-50 to-emerald-100/50',
        ringColor: 'emerald'
    },
    intermediate: { 
        icon: TrendingUp, 
        color: 'blue', 
        gradient: 'from-blue-500 to-blue-600',
        bgGradient: 'from-blue-50 to-blue-100/50',
        ringColor: 'blue'
    },
    advanced: { 
        icon: Target, 
        color: 'purple', 
        gradient: 'from-purple-500 to-purple-600',
        bgGradient: 'from-purple-50 to-purple-100/50',
        ringColor: 'purple'
    },
    expert: { 
        icon: Award, 
        color: 'amber', 
        gradient: 'from-amber-500 to-amber-600',
        bgGradient: 'from-amber-50 to-amber-100/50',
        ringColor: 'amber'
    }
};

export const LearningPathPage = () => {
    const { level: levelParam } = useParams();
    const { t, language, getLocalizedField } = useLanguage();
    const [levels, setLevels] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [lessons, setLessons] = useState([]);
    const [progress, setProgress] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, [levelParam]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [levelsRes, chaptersRes, lessonsRes, progressRes] = await Promise.all([
                axios.get(`${API}/levels`),
                axios.get(`${API}/chapters`),
                axios.get(`${API}/lessons`),
                axios.get(`${API}/progress`)
            ]);

            setLevels(levelsRes.data);
            setChapters(chaptersRes.data);
            setLessons(lessonsRes.data);
            setProgress(progressRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const isLessonCompleted = (lessonId) => {
        return progress.some(p => p.lesson_id === lessonId && p.completed);
    };

    const getLevelProgress = (levelId) => {
        const levelChapters = chapters.filter(c => c.level_id === levelId);
        const chapterIds = levelChapters.map(c => c.id);
        const levelLessons = lessons.filter(l => chapterIds.includes(l.chapter_id));
        const completedCount = levelLessons.filter(l => isLessonCompleted(l.id)).length;
        return levelLessons.length > 0 ? Math.round((completedCount / levelLessons.length) * 100) : 0;
    };

    const getChapterLessons = (chapterId) => {
        return lessons.filter(l => l.chapter_id === chapterId).sort((a, b) => a.order - b.order);
    };

    const getLevelChapters = (levelId) => {
        return chapters.filter(c => c.level_id === levelId).sort((a, b) => a.order - b.order);
    };

    const canAccessLesson = (lesson, chapterLessons) => {
        if (!lesson.is_locked) return true;
        const lessonIndex = chapterLessons.findIndex(l => l.id === lesson.id);
        if (lessonIndex === 0) return true;
        const previousLesson = chapterLessons[lessonIndex - 1];
        return isLessonCompleted(previousLesson.id);
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-48 rounded-2xl" />
                        ))}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // Show level selection if no level param
    if (!levelParam) {
        return (
            <DashboardLayout>
                <div className="space-y-8 animate-fade-in">
                    <div>
                        <h1 className="text-3xl font-heading font-bold text-slate-900">
                            {t('learningPath')}
                        </h1>
                        <p className="text-slate-500 mt-2">
                            {language === 'id' 
                                ? 'Pilih level untuk memulai perjalanan trading Anda'
                                : 'Choose a level to start your trading journey'
                            }
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {levels.map((level) => {
                            const config = levelConfig[level.name.toLowerCase()] || levelConfig.basic;
                            const Icon = config.icon;
                            const progressValue = getLevelProgress(level.id);
                            const levelChapters = getLevelChapters(level.id);
                            const totalLessons = levelChapters.reduce((acc, c) => acc + getChapterLessons(c.id).length, 0);

                            return (
                                <Link key={level.id} to={`/learning/${level.name.toLowerCase()}`}>
                                    <div 
                                        className={`level-card level-${level.name.toLowerCase()} p-6 h-full`}
                                        data-testid={`level-select-${level.name.toLowerCase()}`}
                                    >
                                        <div className="flex items-start gap-5">
                                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center flex-shrink-0 shadow-lg`}>
                                                <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-xl font-heading font-bold text-slate-900">
                                                    {getLocalizedField(level, 'name')}
                                                </h3>
                                                <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                                                    {getLocalizedField(level, 'description')}
                                                </p>
                                                <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
                                                    <span>{levelChapters.length} {t('chapters')}</span>
                                                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                                                    <span>{totalLessons} {t('lessons')}</span>
                                                </div>
                                            </div>
                                            <ProgressRing 
                                                progress={progressValue}
                                                size={80}
                                                strokeWidth={6}
                                                color={config.ringColor}
                                                className="hidden md:flex"
                                            />
                                        </div>
                                        <div className="mt-6 flex items-center justify-between">
                                            <div className="flex-1 mr-4">
                                                <div className="progress-futuristic">
                                                    <div 
                                                        className="progress-futuristic-bar"
                                                        style={{ width: `${progressValue}%` }}
                                                    />
                                                </div>
                                            </div>
                                            {progressValue === 100 ? (
                                                <div className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                                                    <CheckCircle className="w-4 h-4" />
                                                    {t('completed')}
                                                </div>
                                            ) : (
                                                <ChevronRight className="w-5 h-5 text-slate-400" />
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    // Show selected level content
    const currentLevel = levels.find(l => l.name.toLowerCase() === levelParam?.toLowerCase());
    if (!currentLevel) {
        return (
            <DashboardLayout>
                <div className="text-center py-12">
                    <p className="text-slate-500">Level not found</p>
                    <Link to="/learning">
                        <Button className="mt-4">{t('back')}</Button>
                    </Link>
                </div>
            </DashboardLayout>
        );
    }

    const config = levelConfig[currentLevel.name.toLowerCase()] || levelConfig.basic;
    const Icon = config.icon;
    const levelProgress = getLevelProgress(currentLevel.id);

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${config.bgGradient}`}>
                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                        <Link to="/learning">
                            <Button variant="ghost" size="sm" className="w-fit -ml-2">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                {t('back')}
                            </Button>
                        </Link>
                        <div className="flex items-center gap-4 flex-1">
                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center shadow-lg`}>
                                <Icon className="w-7 h-7 text-white" strokeWidth={1.5} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-heading font-bold text-slate-900">
                                    {getLocalizedField(currentLevel, 'name')}
                                </h1>
                                <p className="text-slate-500 text-sm mt-1">
                                    {getLocalizedField(currentLevel, 'description')}
                                </p>
                            </div>
                        </div>
                        <ProgressRing 
                            progress={levelProgress}
                            size={90}
                            strokeWidth={7}
                            color={config.ringColor}
                        />
                    </div>
                </div>

                {/* Chapters & Lessons */}
                <div className="space-y-4">
                    {getLevelChapters(currentLevel.id).map((chapter) => {
                        const chapterLessons = getChapterLessons(chapter.id);
                        const completedLessons = chapterLessons.filter(l => isLessonCompleted(l.id)).length;
                        const chapterProgress = chapterLessons.length > 0 
                            ? Math.round((completedLessons / chapterLessons.length) * 100) 
                            : 0;

                        return (
                            <Accordion 
                                key={chapter.id} 
                                type="single" 
                                collapsible
                                defaultValue={chapter.id}
                            >
                                <AccordionItem 
                                    value={chapter.id}
                                    className="glass-card rounded-2xl overflow-hidden border-0"
                                    data-testid={`chapter-${chapter.id}`}
                                >
                                    <AccordionTrigger className="px-6 py-5 hover:no-underline hover:bg-slate-50/50">
                                        <div className="flex items-center gap-4 text-left w-full">
                                            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-white font-bold shadow-md`}>
                                                {chapter.order}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-heading font-semibold text-slate-900 text-lg">
                                                    {getLocalizedField(chapter, 'title')}
                                                </h3>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <span className="text-sm text-slate-500">
                                                        {completedLessons}/{chapterLessons.length} {t('lessons').toLowerCase()}
                                                    </span>
                                                    <div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                                        <div 
                                                            className={`h-full rounded-full bg-gradient-to-r ${config.gradient} transition-all duration-500`}
                                                            style={{ width: `${chapterProgress}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-6 pb-5">
                                        <div className="space-y-2 mt-2">
                                            {chapterLessons.map((lesson) => {
                                                const completed = isLessonCompleted(lesson.id);
                                                const canAccess = canAccessLesson(lesson, chapterLessons);

                                                return (
                                                    <Link
                                                        key={lesson.id}
                                                        to={canAccess ? `/lesson/${lesson.id}` : '#'}
                                                        className={`block ${!canAccess ? 'cursor-not-allowed' : ''}`}
                                                        onClick={(e) => !canAccess && e.preventDefault()}
                                                    >
                                                        <div 
                                                            className={`flex items-center gap-4 p-4 rounded-xl transition-all duration-300 ${
                                                                canAccess 
                                                                    ? 'hover:bg-slate-50 hover:shadow-sm' 
                                                                    : 'bg-slate-50/50 opacity-50'
                                                            } ${completed ? 'lesson-complete' : ''}`}
                                                            data-testid={`lesson-${lesson.id}`}
                                                        >
                                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                                                                completed 
                                                                    ? 'bg-emerald-100 text-emerald-600'
                                                                    : canAccess
                                                                        ? 'bg-slate-100 text-slate-600 hover:bg-primary/10 hover:text-primary'
                                                                        : 'bg-slate-100 text-slate-400'
                                                            }`}>
                                                                {completed ? (
                                                                    <CheckCircle className="w-5 h-5" />
                                                                ) : canAccess ? (
                                                                    <Play className="w-5 h-5" />
                                                                ) : (
                                                                    <Lock className="w-5 h-5" />
                                                                )}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="font-medium text-slate-900">
                                                                    {getLocalizedField(lesson, 'title')}
                                                                </h4>
                                                                <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                                                                    <Clock className="w-3.5 h-3.5" />
                                                                    {lesson.duration_minutes} {t('minutes')}
                                                                </p>
                                                            </div>
                                                            {canAccess && !completed && (
                                                                <Sparkles className="w-4 h-4 text-primary/50" />
                                                            )}
                                                            {canAccess && (
                                                                <ChevronRight className="w-5 h-5 text-slate-400" />
                                                            )}
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        );
                    })}
                </div>
            </div>
        </DashboardLayout>
    );
};
